import { Box, BoxProps, Flex } from '@chakra-ui/react'
import {
  ContentNodeUtils,
  FilterCondition,
  InputValueContentNode,
} from '@helo/cms'
import { useEffect, useState } from 'react'

import { DateTimeFilterInput } from '../../gql/graphql'
import { FilterCriteria } from '../../services/queryService'
import {
  DATE_FILTER_CONDITION_OPTIONS,
  DATE_FILTER_CONDITION_OPTIONS_EXTENDED,
} from '../FilterEditor/dateFilterConditionOptions'
import { ButtonRadio, ButtonRadioOption } from '../inputs/ButtonRadio'
import { FilterFormProps } from '../inputs/FilterFormTypes'
import { RangeCalendar } from './RangeCalendar'
import { SingleDateCalendar } from './SingleDateCalendar'

export type DateFilterEditorProps = BoxProps &
  FilterFormProps & {
    /** When true, use extended date conditions (before/after/excludes) in ButtonRadio and show SingleDateCalendar for them; when false (default), legacy 4 options and pills in calendar. */
    dateConditionsInDropdown?: boolean
  }

export const defaultFilterCriteria: FilterCriteria = {
  columnId: '',
  filterValue: '',
  filterToValue: '',
  filterValueType: FilterCondition.equals as any,
  condition: FilterCondition.equals,
  operator: FilterCondition.equals as any,
}

export const filterCriteriaToInput = (
  criteria: FilterCriteria,
): DateTimeFilterInput => {
  const { condition, filterValue, filterToValue } = criteria

  switch (condition) {
    case FilterCondition.equals:
      return { equals: filterValue as string }
    case FilterCondition.notEqual:
      return { not: { equals: filterValue as string } }
    case FilterCondition.lessThan:
      return { lt: filterValue as string }
    case FilterCondition.greaterThan:
      return { gt: filterValue as string }
    case FilterCondition.lessThanOrEqual:
      return { lte: filterValue as string }
    case FilterCondition.greaterThanOrEqual:
      return { gte: filterValue as string }
    case FilterCondition.inRange:
      return {
        gte: filterValue as string,
        lte: filterToValue as string,
      }
    case FilterCondition.blank:
      return { equals: null }
    case FilterCondition.notBlank:
      return { not: { equals: null } }
    default:
      return {}
  }
}

export const inputToFilterCriteria = (
  input: DateTimeFilterInput | undefined,
): FilterCriteria => {
  if (!input || Object.keys(input).length === 0) {
    return defaultFilterCriteria
  }

  if (input.equals !== undefined && input.equals !== null) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.equals,
      condition: FilterCondition.equals,
    }
  }
  if (input.equals === null) {
    return {
      ...defaultFilterCriteria,
      condition: FilterCondition.blank,
    }
  }
  if (input.not?.equals !== undefined && input.not?.equals !== null) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.not.equals,
      condition: FilterCondition.notEqual,
    }
  }
  if (input.not?.equals === null) {
    return {
      ...defaultFilterCriteria,
      condition: FilterCondition.notBlank,
    }
  }
  if (input.lt !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.lt,
      condition: FilterCondition.lessThan,
    }
  }
  if (input.gt !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gt,
      condition: FilterCondition.greaterThan,
    }
  }

  if (input.gte !== undefined && input.lte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gte,
      filterToValue: input.lte,
      condition: FilterCondition.inRange,
    }
  }
  if (input.lte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.lte,
      condition: FilterCondition.lessThanOrEqual,
    }
  }
  if (input.gte !== undefined) {
    return {
      ...defaultFilterCriteria,
      filterValue: input.gte,
      condition: FilterCondition.greaterThanOrEqual,
    }
  }

  return defaultFilterCriteria
}

export const DateFilterEditor = (props: DateFilterEditorProps) => {
  const {
    inputValueContentNode,
    onUpdate = () => {},
    isDisabled = false,
    dateConditionsInDropdown = false,
  } = props

  const options: ButtonRadioOption[] = dateConditionsInDropdown
    ? DATE_FILTER_CONDITION_OPTIONS_EXTENDED
    : DATE_FILTER_CONDITION_OPTIONS

  const filterCriteria = inputToFilterCriteria(
    inputValueContentNode.data as DateTimeFilterInput | undefined,
  )

  const [dateMode, setdateMode] = useState<FilterCondition>(() => {
    if (
      filterCriteria?.condition === FilterCondition.notBlank ||
      filterCriteria?.condition === FilterCondition.blank
    ) {
      return filterCriteria.condition
    }

    if (filterCriteria?.condition === FilterCondition.inRange) {
      return FilterCondition.inRange
    }

    if (filterCriteria?.condition === FilterCondition.equals) {
      return FilterCondition.equals
    }

    if (dateConditionsInDropdown) {
      if (filterCriteria?.condition === FilterCondition.lessThan)
        return FilterCondition.lessThan
      if (filterCriteria?.condition === FilterCondition.greaterThan)
        return FilterCondition.greaterThan
      if (filterCriteria?.condition === FilterCondition.notEqual)
        return FilterCondition.notEqual
    }

    return FilterCondition.equals
  })

  const handleUpdate = (updatedCriteria: FilterCriteria) => {
    const dateTimeFilterInput = filterCriteriaToInput(updatedCriteria)
    const updatedContentNode = ContentNodeUtils.updateContentNode(
      dateTimeFilterInput,
    )(inputValueContentNode) as InputValueContentNode
    onUpdate(updatedContentNode)
  }

  useEffect(() => {
    if (
      filterCriteria?.condition === FilterCondition.inRange &&
      filterCriteria.filterValue &&
      filterCriteria.filterToValue &&
      filterCriteria.filterValue > filterCriteria.filterToValue
    ) {
      const swapped = {
        ...filterCriteria,
        filterToValue: filterCriteria.filterValue,
        filterValue: filterCriteria.filterToValue,
      }
      handleUpdate(swapped)
    }
  }, [])

  return (
    <Flex flexDirection="column" gap="8px" margin="-8px">
      <ButtonRadio
        value={dateMode}
        isDisabled={isDisabled}
        onChange={condition => {
          const newCondition = condition as FilterCondition
          const isNewModeBlankOrPresent =
            newCondition === FilterCondition.notBlank ||
            newCondition === FilterCondition.blank

          // Persist immediately when switching TO blank/present
          if (isNewModeBlankOrPresent) {
            handleUpdate({
              ...filterCriteria,
              condition: newCondition,
            })
          } else {
            // When switching condition (e.g. Date ↔ Is Before/After), persist current date value with new condition so URL/store stays in sync
            const hasSingleDateValue =
              filterCriteria.filterValue &&
              typeof filterCriteria.filterValue === 'string'
            const hasRangeValues =
              filterCriteria.filterValue &&
              filterCriteria.filterToValue &&
              newCondition === FilterCondition.inRange
            if (hasSingleDateValue || hasRangeValues) {
              handleUpdate({
                ...filterCriteria,
                condition: newCondition,
              })
            }
          }
          setdateMode(newCondition)
        }}
        options={options}
      />
      {(() => {
        const showSingleDate =
          dateMode === FilterCondition.equals ||
          (dateConditionsInDropdown &&
            (dateMode === FilterCondition.lessThan ||
              dateMode === FilterCondition.greaterThan ||
              dateMode === FilterCondition.notEqual))
        return showSingleDate ? (
          <Box
            pointerEvents={isDisabled ? 'none' : undefined}
            opacity={isDisabled ? 0.6 : 1}
          >
            <SingleDateCalendar
              filterCriteria={{
                ...filterCriteria,
                condition: dateMode,
              }}
              onUpdate={handleUpdate}
              columnId=""
              showConditionPills={!dateConditionsInDropdown}
            />
          </Box>
        ) : null
      })()}
      {dateMode === FilterCondition.inRange ? (
        <Box
          pointerEvents={isDisabled ? 'none' : undefined}
          opacity={isDisabled ? 0.6 : 1}
        >
          <RangeCalendar
            filterCriteria={filterCriteria}
            onUpdate={handleUpdate}
            columnId=""
          />
        </Box>
      ) : null}
    </Flex>
  )
}
